import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Get the intended destination from state, default to /admin
  const from = (location.state as any)?.from?.pathname || '/admin';

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const result = await authService.requestOtp(email);
      setMessage(result.message || 'OTP sent successfully! Check your email (or console in dev mode).');
      setStep('otp');
    } catch (err: any) {
      setError(err.message || 'Failed to request OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      await authService.verifyOtp(email, otpCode);
      setMessage('Login successful!');
      
      // Navigate to the intended destination
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 500);
    } catch (err: any) {
      setError(err.message || 'Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setOtpCode('');
    setError('');
    setMessage('');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <h1>Temple Slides Login</h1>
        <p className="subtitle">Admin & Operator Access</p>

        {error && (
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        {message && (
          <div className="alert alert-success">
            {message}
          </div>
        )}

        {step === 'email' ? (
          <form onSubmit={handleRequestOtp} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                disabled={loading}
                autoFocus
              />
              <small className="form-help">
                Enter your authorized email address to receive a one-time password.
              </small>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Sending...' : 'Send OTP'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="login-form">
            <div className="form-group">
              <label htmlFor="otp">One-Time Password</label>
              <input
                id="otp"
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                required
                disabled={loading}
                autoFocus
                maxLength={6}
                pattern="[0-9]{6}"
              />
              <small className="form-help">
                Enter the 6-digit code sent to <strong>{email}</strong>
              </small>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading || otpCode.length !== 6}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleBackToEmail}
              disabled={loading}
            >
              Back to Email
            </button>
          </form>
        )}

        <div className="login-info">
          <h3>Development Mode Note</h3>
          <p>
            If running in development mode, check the console logs for the OTP code.
            In production, the OTP will be sent to your email.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
